import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { BasePageState } from '../../types/base';
import Toast from '@components/Toast';
import { applyDefaultSEO, applySEO, buildThreadDetailSEO } from '@utils/seo';
import { setThemeColor, toRgba, hexToRgb, getThemeColor } from '@utils/colorConversion';

interface RouteParams {
    id: string;
}
interface ThreadDetailProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
}
interface ThreadDetailState extends BasePageState {
    loading: false,
    error: null,
    pageLoaded: false,
    myValue: string;
    threadAuthorInfo: any
    isVivoBrowserStatus: boolean
    themeColor: any
    serviceStatus: boolean    // 客服反馈入口是否显示
    sendConfirmPopupStatus: boolean // 客服反馈是否已解决问题确认弹框
    satisfactionLevel?: any  // 解决问题的满意程度
    currentSatisfactionIndex?: any  // 当前解决问题的满意程度
    judgeTextValue?: any    // 本次反馈服务的评价或建议
}
@inject('app', 'site', 'user', 'thread')
@observer
class threadDetail extends BasePage<ThreadDetailProps, ThreadDetailState> {
    threadId: any = '';  // 帖子id
    namespace = 'postlist';
    state: ThreadDetailState = {
        loading: false,
        error: null,
        pageLoaded: false,
        myValue: '',
        threadAuthorInfo: null,   // 帖子作者信息，显示在PC右侧侧边栏
        isVivoBrowserStatus: false, // 是否是vivo浏览器
        themeColor: '',  //主题色
        serviceStatus: true,    // 客服反馈入口是否显示
        sendConfirmPopupStatus: false,  // 客服反馈是否已解决问题确认弹框
        satisfactionLevel: [
            {text: '极差', id: 1, icon: '/feedback/judge1'},
            {text: '不满意', id: 2, icon: '/feedback/judge2'},
            {text: '一般', id: 3, icon: '/feedback/judge3'},
            {text: '满意', id: 4, icon: '/feedback/judge4'},
            {text: '非常满意', id: 5, icon: '/feedback/judge5'},
        ],  // 解决问题的满意程度
        currentSatisfactionIndex: 100,    // 当前解决问题的满意程度
        judgeTextValue: '',    // 本次反馈服务的评价或建议
    };
    componentDidMount() {
        applyDefaultSEO();
        const { app, user, thread, match } = this.props;
        
        this.threadId = parseInt(match.params.id);
        const routerParams = new URLSearchParams(this.props.location.search);
        const code: any = routerParams?.get('code') || '';
        const currentPostId: any = routerParams?.get('postId') || '';
        this.getCommentDetail(currentPostId)
        this.getThreadDetail(code);
        super.componentDidMount();
        // 判断是不是vivo浏览器
        this.isVivoBrowser();
    }

    componentWillUnmount() {
        const { app, user, thread } = this.props;
        thread.setThreadData(null)
    }

    // 获取路由上postId的评论详情
    getCommentDetail = async (postId) => {
        if (!postId) return;

        const { thread } = this.props;
        const res = await thread.getCommentDetail({ postId });
        const currentPostDetail = res?.data;
        if (!currentPostDetail) return;

        // 从 postList 第一页中删除与当前评论 id 重复的数据，避免页面重复展示
        const listData = thread?.listStore?.lists?.['postList']?.data;
        if (!listData) return;

        const pageKeys = Object.keys(listData).map(Number).sort((a, b) => a - b);
        const firstPageKey = pageKeys.length > 0 ? String(pageKeys[0]) : null;
        if (!firstPageKey || !Array.isArray(listData[firstPageKey])) return;

        const targetId = currentPostDetail.id;
        const firstPage = listData[firstPageKey];
        const existsIndex = firstPage.findIndex((item: any) => item?.id === targetId);

        if (existsIndex !== -1) {
            // 从第一页中移除重复数据
            listData[firstPageKey] = [
                ...firstPage.slice(0, existsIndex),
                ...firstPage.slice(existsIndex + 1),
            ];
        }

        // 将查询到的评论详情追加到第一页的第一条
        listData[firstPageKey] = [currentPostDetail, ...listData[firstPageKey]];
    }

    /**
     * 判断当前浏览器是否为 vivo 浏览器
     * @returns {boolean} true: 是 vivo 浏览器, false: 不是
     */
    isVivoBrowser() {
        if (typeof navigator === 'undefined') {
            return false;
        }
        
        const ua = navigator.userAgent.toLowerCase();
        // 优先判断 VivoBrowser 关键词，其次判断 vivo 关键词作为兼容
        let isVivoBrowser = /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            this.setState({
                isVivoBrowserStatus: isVivoBrowser
            })
        return /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            
    }

    //点赞帖子/评论（不刷新详情，直接更新 MobX）
    likeClick = async (data: any, type: 'thread' | 'post') => {
        const { thread } = this.props;
        const id = thread?.getId ? thread.getId(data) : data?.id;
        console.log('likeClick 被调用:', { data, type, id, currentIsLiked: data.isLiked });
        if (thread && id) {
            // 通过 action 方法立即更新isLiked状态以获得更好的用户体验
            const isLiked = !data.isLiked;
            if (type === 'thread') {
                thread.updateLikeStatus(isLiked);
            } else if (type === 'post') {
                // console.log('开始乐观更新评论点赞状态:', { namespace: 'postList', id, isLiked });
                const updated = thread.listStore.updateListItem?.({
                    namespace: 'postList',
                    id,
                    updates: { isLiked }
                });
                console.log('乐观更新结果:', updated);
            }
            const params = {
                id,
                type,
                isLiked: isLiked,
                likeCount: data.likeCount
            };
            console.log('准备调用 API:', params);
            // 然后调用 API，API会返回最新的 likeCount
            const result = await thread.createUserslikes(params);
            console.log('API 返回结果:', result);
        }
    }

    //收藏（不刷新详情）
    favoriteClick = async (status: boolean) => {
        const { thread, match } = this.props as any;
        const threadId = parseInt(match.params.id);
        if (thread && threadId) {
            // 通过 action 方法立即更新isFavorite状态以获得更好的用户体验
            const newStatus = !status;
            thread.updateFavoriteStatus(newStatus);
            const params = {
                isFavorite: newStatus,
                id: threadId,
                type: 'thread',
            } as any;
            // 然后调用 API，API会返回最新的 favoriteCount
            await thread.threadFavorite(params);
        }
    }

    //分享
    shareClick = () => {
        const { site, thread, match } = this.props as any;
        const threadData: any = thread?.threadData;
        const id = match?.params?.id;
        
        const base = site?.apiBase;
        const htmlContent = threadData.content || '';
        const plainText = htmlContent.replace(/<[^>]+>/g, '').trim();
        const imgCount = (htmlContent.match(/<img/gi) || []).length;
        const imgLabel = '[图片]'.repeat(imgCount);
        let contentSnippet: string;
        if (threadData.title) {
            // 有标题就取标题，限制40字符
            contentSnippet = threadData.title.length > 40 ? threadData.title.slice(0, 40) + '...' : threadData.title;
        } else if (plainText) {
            // 没有标题取内容，限制40字符
            contentSnippet = plainText.length > 40 ? plainText.slice(0, 40) + '...' : plainText;
        } else {
            contentSnippet = imgLabel || '';
        }
        const truncatedContent = contentSnippet;
        const url = `@${threadData.user?.nickname}发表内容：${truncatedContent}\r\n${base}/threadDetail/${id}\r\n复制本消息打开vivo社区App即可查看详情~`;
        try {
            const { copy } = require('@utils/copy');
            copy(url);
            // Toast 在各子组件内已有，这里简化
            Toast.info('链接复制成功，快去分享好友吧～')
            thread.createThreadShare({threadId: match?.params?.id})
        } catch (_) {
            if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url);
        }
    }
    // 问题反馈，我也遇到相同问题
    submitFeedbackSame = async () => {
        const { thread, match } = this.props as any;
        const threadData: any = thread?.threadData;
        if(thread) {
            const res = await thread.submitFeedbackSame(threadData?.feedback?.id, {isSame: !threadData?.feedback?.isSame})
            if(res.code == 0) {
                Toast.info(threadData?.feedback?.isSame ? '取消反馈成功' :  '反馈成功')
            }
        }
        
    }
    // 获取帖子详情
    getThreadDetail = (code = '') => {
        const {thread, user } = this.props;
        if (thread) {
            // 如果有code参数，将其作为对象传给后端
            const params = code ? { code } : {};
            thread.getThreadDetail(this.threadId, params).then(res => {
                console.log(res, '详情接口返回');
                if(res.code == '403' || res.code == '404') {
                    let infoValue = '';
                    if(res.code == '403') { // 帖子状态异常
                        infoValue = '资源不存在';
                    } else if(res.code == '404') {
                        infoValue = '资源不存在';
                    }
                    Toast.info(infoValue);
                    setTimeout(() => {
                        // if (window.history.length > 1) {
                        //     // this.props.history.goBack();
                        // } else {
                        //     this.props.history.push('/');
                        // } 
                        window.ensureBack(
                            () => {
                            //   console.log('返回完成');
                            }
                          );
                    }, 1000)
                    
                } else if(res.code == 0) {
                    if(res.data?.lotteryActivities?.length > 0) {
                        // 设置主题色 - setThemeColor 会同时设置 --lottery-theme-color 和 --lottery-theme-color-rgb
                        const color = res.data?.lotteryActivities?.[0].foregroundColor;
                        if (color) {
                            setThemeColor(color, 'lottery-theme-color');
                            this.setState({
                                themeColor: color
                            });
                        }
                    }
                    
                    applySEO(buildThreadDetailSEO(res.data));
                    if (user) {
                        user.getUserInfo({ code: res.data.user?.code }).then(res => {
                            console.log(res.data, '获取当前路由上帖子的的作者信息')
                            this.setState({
                                threadAuthorInfo: res.data
                            })
                        });
                    }
                }
            });
        }
    }

    componentDidUpdate(prevProps: any) {
        // 监听路由参数变化，如果话题 ID 改变，重新加载数据
        if (prevProps.match.params.id !== this.props.match.params.id) {
            applyDefaultSEO();
            const { thread, match } = this.props as any;
            this.threadId = this.props.match.params.id;
            
            // 获取URL中的code参数
            const routerParams = new URLSearchParams(this.props.location.search);
            const code: any = routerParams?.get('code') || '';
            const params = code ? { code } : {};
            
            if (thread) {
                thread.getThreadDetail(this.threadId, params)
                this.getThreadListData();
            }
            
        }
    }

    // 跳转到话题详情页
    handleTopicClick = (id: string | number) => {
        this.props.history && this.props.history.push(`/topicDetail/${id}`);
    };
    

    // 关注用户
    createFollowUser = () => {
        console.log('关注');
        const { user, thread } = this.props;
        if (thread) {
            let params: any = {
                toUserCode: (thread?.threadData as any)?.user?.code
            }
            thread.createFollowUser(params)
        }
    }

    // 取消关注用户
    followDelete = () => {
        console.log('取消关注用户');
        const { user, thread } = this.props;
        if (thread) {
            let params: any = {
                code: (thread?.threadData as any)?.user?.code,
                type: 1
            }
            thread.followDelete(params)
        }
    }
    // 投票
    createActivityVote= (option) => {
        console.log(option, '投票事件');
        const { thread } = this.props;
        if(thread) {
            const threadData = (thread?.threadData as any);
            let params = {
                ids: option.split(',').map(item => Number(item.trim()))
            }
            
            thread.createVotesOptions(threadData?.votes?.[0].id, params);
            this.getThreadDetail();
            this.getThreadListData();
        }
        
    }
    

    // 修改回复/帖子 - id是评论/帖子Id,actionType 是操作类型 ('delete'|'sink'|'sticky')
    postsUpdate = async (id: any, actionType: 'delete' | 'sink' | 'sticky', type: 'post' | 'comment' = 'comment', currentState?: boolean) => {
        const { thread } = this.props;
        if (!thread) return;
        
        const isDeleted = actionType === 'delete' ? true : undefined;
        const isSink = actionType === 'sink' ? !currentState : undefined;
        const isSticky = actionType === 'sticky' ? !currentState : undefined;
        
        const params: any = { id };
        if (typeof isDeleted !== 'undefined') params.isDeleted = isDeleted;
        if (typeof isSink !== 'undefined') params.isSink = isSink;
        if (typeof isSticky !== 'undefined') params.isSticky = isSticky;

        const res = await thread.updateCommentAndSyncList(params);
        const code = typeof res.code !== 'undefined' ? res.code : res.Code;
        if (code !== 0) throw new Error(res.message || res.Message || '操作失败');
        return res;
    }
    

    // 只获取数据，不渲染
    getThreadListData = () => {
        const { thread } = this.props;
        if (!thread || !thread.listStore) return [];
        const list = thread.listStore.lists[this.namespace];
        if (!list) return [];
        return thread.listStore.getList({ namespace: this.namespace });
    }

    // 用户确认有无帮助confirmState int 1 有帮助 2无帮助
    sendConfirmClick = async (solveStatus: number) => {
        const { thread } = this.props;
        const feedbackId = thread?.threadData?.feedback?.id;
        if (feedbackId && thread) {
            const res = await thread.submitFeedbackConfirmData(feedbackId, { confirmState: solveStatus });
            if(res.code == 0) {
                // 隐藏 serviceIcon 入口
                if (thread.threadData?.feedback) {
                    thread.threadData.feedback.isSendConfirm = false;
                }
                Toast.success('感谢你对我们服务的反馈');
                this.setState({ sendConfirmPopupStatus: false });
            } 
            
        }
        
    };

    // 修改state
      handleState = (data) => {
        this.setState({ ...data });
      }

    render() {
        const { app } = this.props;
        const { threadAuthorInfo, isVivoBrowserStatus, sendConfirmPopupStatus, serviceStatus, satisfactionLevel, currentSatisfactionIndex, judgeTextValue } =this.state;
        const platform = app?.platform || 'h5';
        const PCComp: any = PC as any;
        const H5Comp: any = H5 as any;
        // 如果平台信息还未初始化，显示加载状态
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {
            return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (<PCComp
                    threadId={this.threadId}
                    threadAuthorInfo={threadAuthorInfo}
                    createFollowUser={() => this.createFollowUser()}
                    followDelete={() => this.followDelete()}
                    createActivityVote={this.createActivityVote}
                    likeClick={this.likeClick}
                    favoriteClick={this.favoriteClick}
                    shareClick={this.shareClick}
                    submitFeedbackSame={this.submitFeedbackSame}
                    postsUpdate={this.postsUpdate}
                    history={this.props.history}
                    isVivoBrowserStatus={isVivoBrowserStatus}
                    sendConfirmPopupStatus={sendConfirmPopupStatus}
                    serviceStatus={serviceStatus}
                    sendConfirmClick={this.sendConfirmClick}
                    satisfactionLevel={satisfactionLevel}
                    currentSatisfactionIndex={currentSatisfactionIndex}
                    judgeTextValue={judgeTextValue}
                    handleState={this.handleState}
                ></PCComp>) : (
                    <H5Comp
                        threadId={this.threadId}
                        createFollowUser={() => this.createFollowUser()}
                        followDelete={() => this.followDelete()}
                        createActivityVote={this.createActivityVote}
                        likeClick={this.likeClick}
                        favoriteClick={this.favoriteClick}
                        shareClick={this.shareClick}
                        submitFeedbackSame={this.submitFeedbackSame}
                        postsUpdate={this.postsUpdate}
                        history={this.props.history}
                        isVivoBrowserStatus={isVivoBrowserStatus}
                        sendConfirmPopupStatus={sendConfirmPopupStatus}
                        serviceStatus={serviceStatus}
                        sendConfirmClick={this.sendConfirmClick}
                        satisfactionLevel={satisfactionLevel}
                        currentSatisfactionIndex={currentSatisfactionIndex}
                        judgeTextValue={judgeTextValue}
                        handleState={this.handleState}
                    ></H5Comp>
                )}
            </div>)
        }

    }
}

export default withRouter(threadDetail);
